import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { downloadFile, formatINR, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Search, FileSpreadsheet, FileText, Pencil, Trash2, Phone, MapPin, BookOpen, MessageCircle } from "lucide-react";
import CustomerLedgerSheet from "@/components/CustomerLedgerSheet";
import { openWhatsApp, pendingBillsMessage } from "@/lib/whatsapp";

const empty = { name: "", phone: "", area: "", address: "" };

export default function Customers() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [area, setArea] = useState("all");
  const [sort, setSort] = useState("name");
  const [order, setOrder] = useState("asc");
  const [dialog, setDialog] = useState(false);
  const [ledgerFor, setLedgerFor] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const { data: customers = [], isLoading } = useQuery({
    queryKey: ["customers", search, area, sort, order],
    queryFn: async () => (await api.get("/customers", { params: { search, area, sort, order } })).data,
  });
  const { data: areas = [] } = useQuery({ queryKey: ["areas"], queryFn: async () => (await api.get("/customers/areas")).data });

  const save = useMutation({
    mutationFn: async () => {
      if (editing) return api.put(`/customers/${editing}`, form);
      return api.post("/customers", form);
    },
    onSuccess: () => {
      toast.success(editing ? "Customer updated" : "Customer added");
      qc.invalidateQueries({ queryKey: ["customers"] });
      qc.invalidateQueries({ queryKey: ["areas"] });
      setDialog(false);
      setForm(empty);
      setEditing(null);
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/customers/${id}`),
    onSuccess: () => {
      toast.success("Customer removed");
      qc.invalidateQueries({ queryKey: ["customers"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const openEdit = (c) => {
    setEditing(c.id);
    setForm({ name: c.name, phone: c.phone, area: c.area, address: c.address });
    setDialog(true);
  };
  const openNew = () => {
    setEditing(null);
    setForm(empty);
    setDialog(true);
  };

  const sharePending = async (c) => {
    if (!c.phone) {
      toast.error("No phone number for this customer");
      return;
    }
    try {
      const { data: deliveries } = await api.get("/deliveries");
      const pending = deliveries.filter((d) => d.customer_id === c.id && d.status !== "Cancelled" && (d.outstanding || 0) > 0.01);
      if (!pending.length) {
        toast.info(`${c.name} has no pending bills`);
        return;
      }
      openWhatsApp(c.phone, pendingBillsMessage(c, pending));
    } catch {
      toast.error("Could not load pending bills");
    }
  };

  const doExport = async (fmt) => {
    try {
      await downloadFile(
        `/customers/export?format=${fmt}&search=${encodeURIComponent(search)}&area=${encodeURIComponent(area)}&sort=${sort}&order=${order}`,
        `customers.${fmt === "pdf" ? "pdf" : "xlsx"}`
      );
      toast.success(`Exported ${fmt.toUpperCase()}`);
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Customers</h1>
          <p className="mt-1 text-sm text-muted-foreground">{customers.length} customers · filter, sort &amp; export</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => doExport("excel")} data-testid="customer-export-excel">
            <FileSpreadsheet className="mr-2 h-4 w-4" /> Excel
          </Button>
          <Button variant="outline" onClick={() => doExport("pdf")} data-testid="customer-export-pdf">
            <FileText className="mr-2 h-4 w-4" /> PDF
          </Button>
          <Button onClick={openNew} data-testid="customer-add-button">
            <Plus className="mr-2 h-4 w-4" /> Add Customer
          </Button>
        </div>
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Search name, phone, area…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="customer-search-input"
            />
          </div>
          <Select value={area} onValueChange={setArea}>
            <SelectTrigger data-testid="customer-area-filter"><SelectValue placeholder="Area" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Areas</SelectItem>
              {areas.map((a) => (
                <SelectItem key={a} value={a}>{a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="customer-sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Sort: Name</SelectItem>
              <SelectItem value="area">Sort: Area</SelectItem>
              <SelectItem value="orders">Sort: Orders</SelectItem>
              <SelectItem value="outstanding">Sort: Outstanding</SelectItem>
            </SelectContent>
          </Select>
          <Select value={order} onValueChange={setOrder}>
            <SelectTrigger data-testid="customer-order-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="asc">Ascending</SelectItem>
              <SelectItem value="desc">Descending</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/60">
                <TableHead>Customer</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Area</TableHead>
                <TableHead className="text-right">Orders</TableHead>
                <TableHead className="text-right">Business</TableHead>
                <TableHead className="text-right">Outstanding</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={7} className="py-12 text-center text-muted-foreground">Loading…</TableCell></TableRow>
              ) : customers.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="py-12 text-center text-muted-foreground">No customers found</TableCell></TableRow>
              ) : (
                customers.map((c, i) => (
                  <motion.tr
                    key={c.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: Math.min(i * 0.02, 0.3) }}
                    className="border-b transition-colors hover:bg-secondary/40"
                    data-testid={`customer-row-${c.id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{c.name}</span>
                        <span className="rounded bg-secondary px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">{c.code}</span>
                      </div>
                      <div className="text-xs text-muted-foreground">{c.address}</div>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1.5 font-mono text-sm"><Phone className="h-3 w-3 text-muted-foreground" />{c.phone || "—"}</span>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1.5 text-sm"><MapPin className="h-3 w-3 text-muted-foreground" />{c.area || "—"}</span>
                    </TableCell>
                    <TableCell className="text-right font-mono">{c.orders}</TableCell>
                    <TableCell className="text-right font-mono">{formatINR(c.total_business)}</TableCell>
                    <TableCell className="text-right font-mono">
                      <span className={c.outstanding > 0 ? "text-rose-600 dark:text-rose-400" : "text-muted-foreground"}>
                        {formatINR(c.outstanding)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" title="Share pending bills via WhatsApp" onClick={() => sharePending(c)} data-testid={`customer-whatsapp-${c.id}`}>
                          <MessageCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => setLedgerFor(c)} data-testid={`customer-ledger-${c.id}`}>
                          <BookOpen className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`customer-edit-${c.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {user?.role === "admin" && (
                          <Button variant="ghost" size="icon" onClick={() => del.mutate(c.id)} data-testid={`customer-delete-${c.id}`}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </motion.tr>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <CustomerLedgerSheet customer={ledgerFor} open={!!ledgerFor} onClose={() => setLedgerFor(null)} />

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent data-testid="customer-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Customer" : "Add Customer"}</DialogTitle>
            <DialogDescription>Store the customer name, contact and delivery area.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="customer-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="customer-phone-input" />
              </div>
              <div className="space-y-2">
                <Label>Area / Locality</Label>
                <Select value={form.area} onValueChange={(v) => setForm({ ...form, area: v })}>
                  <SelectTrigger data-testid="customer-area-input"><SelectValue placeholder="Select area" /></SelectTrigger>
                  <SelectContent>
                    {areas.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Full Address</Label>
              <Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="customer-address-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
            <Button onClick={() => save.mutate()} disabled={!form.name || save.isPending} data-testid="customer-save-button">
              {editing ? "Save Changes" : "Add Customer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
